import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from "lucide-react";
import { db } from "../lib/instant";

export default function Cart({ theme }) {
  const { cart, removeFromCart, updateQuantity, cartSubtotal, clearCart } = useCart();
  const primaryColor = theme?.primaryColor || "#6b2118";
  const accentColor = theme?.accentColor || "#c9a84c";
  const secondaryColor = theme?.secondaryColor || "#2e7d32";

  const { data } = db.useQuery({ settings: {} });
  const settings = data?.settings?.[0] || {};
  const gstRate = settings.gstRate || 5;
  const gstAmount = (cartSubtotal * gstRate) / 100;
  const deliveryFee = settings.deliveryFee || (cartSubtotal >= (settings.freeDeliveryAbove || 500) ? 0 : 50);
  const total = cartSubtotal + gstAmount + (cartSubtotal > 0 ? deliveryFee : 0);

  if (cart.length === 0) return (
    <div style={{ paddingTop: 64, minHeight: "100vh", background: "#fdf6e3", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 20 }}>
      <ShoppingBag size={64} color="#ccc" />
      <h2 style={{ color: "#999", fontFamily: "Georgia, serif" }}>Your cart is empty</h2>
      <Link to="/shop" style={{ background: primaryColor, color: "#fff", padding: "12px 32px", borderRadius: 40, textDecoration: "none", fontWeight: 700 }}>Continue Shopping</Link>
    </div>
  );

  return (
    <div style={{ paddingTop: 64, minHeight: "100vh", background: "#fdf6e3" }}>
      <div style={{ background: primaryColor, padding: "48px 5vw 36px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ fontSize: "0.72rem", letterSpacing: 4, textTransform: "uppercase", color: accentColor, fontWeight: 700, marginBottom: 8 }}>Your Cart</div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(1.8rem, 4vw, 3rem)", color: "#fff" }}>Shopping Cart</h1>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 5vw", display: "grid", gridTemplateColumns: "1fr 380px", gap: 32, alignItems: "start" }}>
        {/* Items */}
        <div>
          {cart.map(item => (
            <div key={item.id} style={{ background: "#fff", borderRadius: 16, padding: 20, marginBottom: 16, display: "flex", gap: 20, alignItems: "center", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
              <div style={{ width: 80, height: 80, background: "#fdf6e3", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "2rem" }}>
                {item.imageUrl ? <img src={item.imageUrl} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 12 }} /> : "🌾"}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#2c1810", marginBottom: 4 }}>{item.name}</h3>
                <p style={{ fontSize: "0.8rem", color: "#999", marginBottom: 12 }}>{item.weight || "Health Mix"}</p>
                <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", border: "1px solid #e0d8d0", borderRadius: 8, overflow: "hidden" }}>
                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)} style={{ width: 32, height: 32, background: "none", border: "none", cursor: "pointer", color: primaryColor }}>−</button>
                    <span style={{ width: 32, textAlign: "center", fontWeight: 700, fontSize: "0.9rem" }}>{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)} style={{ width: 32, height: 32, background: "none", border: "none", cursor: "pointer", color: primaryColor }}>+</button>
                  </div>
                  <span style={{ fontWeight: 800, color: primaryColor, fontSize: "1.1rem" }}>₹{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              </div>
              <button onClick={() => removeFromCart(item.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#f44336", padding: 8 }}>
                <Trash2 size={18} />
              </button>
            </div>
          ))}
          <button onClick={clearCart} style={{ background: "none", border: "none", color: "#f44336", cursor: "pointer", fontSize: "0.85rem", marginTop: 8 }}>Clear Cart</button>
        </div>

        {/* Summary */}
        <div style={{ background: "#fff", borderRadius: 20, padding: 28, boxShadow: "0 4px 24px rgba(0,0,0,0.08)", position: "sticky", top: 88 }}>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: "1.4rem", color: "#2c1810", marginBottom: 24 }}>Order Summary</h2>

          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem", color: "#555" }}>
              <span>Subtotal</span><span style={{ fontWeight: 700 }}>₹{cartSubtotal.toFixed(2)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem", color: "#555" }}>
              <span>GST ({gstRate}%)</span><span style={{ fontWeight: 700 }}>₹{gstAmount.toFixed(2)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem", color: "#555" }}>
              <span>Delivery</span>
              <span style={{ fontWeight: 700, color: deliveryFee === 0 ? secondaryColor : "#333" }}>
                {deliveryFee === 0 ? "FREE" : `₹${deliveryFee}`}
              </span>
            </div>
            {deliveryFee > 0 && (
              <p style={{ fontSize: "0.75rem", color: secondaryColor, background: "#f0faf0", padding: "8px 12px", borderRadius: 8 }}>
                Add ₹{((settings.freeDeliveryAbove || 500) - cartSubtotal).toFixed(0)} more for free delivery!
              </p>
            )}
            <div style={{ borderTop: "2px solid #f0e8d0", paddingTop: 14, display: "flex", justifyContent: "space-between", fontSize: "1.1rem", fontWeight: 800, color: "#2c1810" }}>
              <span>Total</span><span>₹{total.toFixed(2)}</span>
            </div>
          </div>

          <Link to="/checkout" style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            background: primaryColor, color: "#fff", padding: "14px 24px",
            borderRadius: 12, textDecoration: "none", fontWeight: 700, fontSize: "1rem",
            marginBottom: 12,
          }}>Proceed to Checkout <ArrowRight size={18} /></Link>
          <Link to="/shop" style={{ display: "block", textAlign: "center", color: primaryColor, textDecoration: "none", fontSize: "0.85rem" }}>← Continue Shopping</Link>
        </div>
      </div>

      <style>{`@media(max-width:900px){.cart-grid{grid-template-columns:1fr!important;}}`}</style>
    </div>
  );
}
